import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export const Knob = ({ label, value, min, max, step = 0.01, unit = "", onChange, testId }) => (
  <label className="block min-w-0">
    <span className="flex justify-between text-[10px] font-mono text-muted-foreground mb-1"><span>{label}</span><span className="text-foreground">{typeof value === "number" ? +value.toFixed(2) : value}{unit}</span></span>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} data-testid={testId} />
  </label>
);

export const Toggle = ({ label, on, onChange, testId }) => (
  <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
    <Switch checked={!!on} onCheckedChange={onChange} className="scale-75" data-testid={testId} /> {label}
  </label>
);

export function ReverbEditor({ value, spaces, onChange, t }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const base = spaces[value.space] || {};
  return (
    <div className="grid sm:grid-cols-4 gap-3 items-end">
      <label className="block"><span className="text-[10px] font-mono text-muted-foreground block mb-1">Espace</span>
        <Select value={value.space} onValueChange={(s) => onChange({ space: s })}>
          <SelectTrigger className="h-8 text-xs bg-background border-border" data-testid={`${t}-reverb-space`}><SelectValue /></SelectTrigger>
          <SelectContent className="glass border-border">{Object.entries(spaces).map(([k, s]) => <SelectItem key={k} value={k}>{s.label} · {s.decay}s</SelectItem>)}</SelectContent>
        </Select></label>
      <Knob label="Mix" value={value.mix ?? base.mix ?? 0.25} min={0} max={1} onChange={(v) => set("mix", v)} testId={`${t}-reverb-mix`} />
      <Knob label="Decay" value={value.decay ?? base.decay ?? 2} min={0.1} max={12} step={0.1} unit="s" onChange={(v) => set("decay", v)} testId={`${t}-reverb-decay`} />
      <Knob label="Damping" value={value.damping ?? base.damping ?? 0.5} min={0} max={1} onChange={(v) => set("damping", v)} testId={`${t}-reverb-damping`} />
    </div>
  );
}

export function StripEditor({ value, bands, defaults, onChange, t }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const eq = bands.map((f) => value.eq?.find((b) => b.f === f) || { f, g: 0, q: 1 });
  const setEq = (f, g) => set("eq", eq.map((b) => (b.f === f ? { ...b, g } : b)).filter((b) => b.g !== 0));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {eq.map((b) => <Knob key={b.f} label={b.f >= 1000 ? `${b.f / 1000}k` : `${b.f}`} value={b.g} min={-12} max={12} step={0.5} unit=" dB" onChange={(g) => setEq(b.f, g)} testId={`${t}-eq-${b.f}`} />)}
      </div>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div className="space-y-1"><Toggle label="Gate" on={!!value.gate} onChange={(on) => set("gate", on ? { ...defaults.gate } : null)} testId={`${t}-gate-toggle`} />
          {value.gate && <Knob label="Seuil" value={value.gate.threshold_db} min={-90} max={0} step={1} unit=" dB" onChange={(v) => set("gate", { ...value.gate, threshold_db: v })} testId={`${t}-gate-threshold`} />}</div>
        <div className="space-y-1"><Toggle label="Compresseur" on={!!value.comp} onChange={(on) => set("comp", on ? { ...defaults.comp } : null)} testId={`${t}-comp-toggle`} />
          {value.comp && <><Knob label="Seuil" value={value.comp.threshold_db} min={-60} max={0} step={1} unit=" dB" onChange={(v) => set("comp", { ...value.comp, threshold_db: v })} testId={`${t}-comp-threshold`} />
            <Knob label="Ratio" value={value.comp.ratio} min={1} max={20} step={0.5} unit=":1" onChange={(v) => set("comp", { ...value.comp, ratio: v })} testId={`${t}-comp-ratio`} /></>}</div>
        <Knob label="Saturation" value={value.saturation || 0} min={0} max={1} onChange={(v) => set("saturation", v)} testId={`${t}-saturation`} />
        <Toggle label="Auto-volume" on={value.auto_volume} onChange={(v) => set("auto_volume", v)} testId={`${t}-autovolume`} />
      </div>
    </div>
  );
}

export function ModulationEditor({ value, types, onChange, t }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid sm:grid-cols-4 gap-3 items-end">
      <label className="block"><span className="text-[10px] font-mono text-muted-foreground block mb-1">Type</span>
        <Select value={value.type} onValueChange={(v) => set("type", v)}>
          <SelectTrigger className="h-8 text-xs bg-background border-border" data-testid={`${t}-mod-type`}><SelectValue /></SelectTrigger>
          <SelectContent className="glass border-border">{Object.entries(types).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select></label>
      <Knob label="Profondeur" value={value.depth} min={0} max={1} onChange={(v) => set("depth", v)} testId={`${t}-mod-depth`} />
      <Knob label="Vitesse" value={value.rate} min={0.05} max={8} step={0.05} unit=" Hz" onChange={(v) => set("rate", v)} testId={`${t}-mod-rate`} />
      <Knob label="Mix" value={value.mix} min={0} max={1} onChange={(v) => set("mix", v)} testId={`${t}-mod-mix`} />
    </div>
  );
}

export function SidechainEditor({ value, voices, onChange, t }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid sm:grid-cols-4 gap-3 items-end">
      <label className="block sm:col-span-2"><span className="text-[10px] font-mono text-muted-foreground block mb-1">Voix (déclenche le ducking)</span>
        <Select value={value.voice_id || ""} onValueChange={(v) => set("voice_id", v)}>
          <SelectTrigger className="h-8 text-xs bg-background border-border" data-testid={`${t}-sc-voice`}><SelectValue placeholder="Choisir une réplique…" /></SelectTrigger>
          <SelectContent className="glass border-border">{voices.map((v) => <SelectItem key={v.id} value={v.id}>#{v.seq} · {(v.text || v.prompt || v.kind || "").slice(0, 40)}</SelectItem>)}</SelectContent>
        </Select></label>
      <Knob label="Seuil" value={value.threshold_db} min={-60} max={0} step={1} unit=" dB" onChange={(v) => set("threshold_db", v)} testId={`${t}-sc-threshold`} />
      <div className="space-y-2"><Knob label="Ratio" value={value.ratio} min={1} max={20} step={0.5} unit=":1" onChange={(v) => set("ratio", v)} testId={`${t}-sc-ratio`} />
        <Toggle label="Mixer la voix" on={value.mix_voice} onChange={(v) => set("mix_voice", v)} testId={`${t}-sc-mixvoice`} /></div>
    </div>
  );
}

const LUFS = [["off", "Off"], ["-14", "-14 (stream)"], ["-16", "-16 (jeu)"], ["-18", "-18 (cinéma)"], ["-23", "-23 (broadcast)"]];
export function LimiterEditor({ value, onChange, t }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid sm:grid-cols-4 gap-3 items-end">
      <Knob label="Plafond" value={value.ceiling_db} min={-12} max={0} step={0.1} unit=" dBTP" onChange={(v) => set("ceiling_db", v)} testId={`${t}-lim-ceiling`} />
      <Knob label="Lookahead" value={value.lookahead_ms} min={0.1} max={80} step={0.1} unit=" ms" onChange={(v) => set("lookahead_ms", v)} testId={`${t}-lim-lookahead`} />
      <label className="block"><span className="text-[10px] font-mono text-muted-foreground block mb-1">Loudness cible</span>
        <Select value={value.lufs == null ? "off" : String(value.lufs)} onValueChange={(v) => set("lufs", v === "off" ? null : Number(v))}>
          <SelectTrigger className="h-8 text-xs bg-background border-border" data-testid={`${t}-lim-lufs`}><SelectValue /></SelectTrigger>
          <SelectContent className="glass border-border">{LUFS.map(([k, l]) => <SelectItem key={k} value={k}>{l} {k !== "off" ? "LUFS" : ""}</SelectItem>)}</SelectContent>
        </Select></label>
      <Toggle label="True-peak (4×)" on={value.true_peak} onChange={(v) => set("true_peak", v)} testId={`${t}-lim-truepeak`} />
    </div>
  );
}
